import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { Blocks } from "react-loader-spinner";
import toast from "react-hot-toast";
import moment from "moment";
import { MdDateRange } from "react-icons/md";
import { 
  getUserLogs, 
  getUserLogsByUserId, 
  getUserLogsByIpAddress 
} from "../../State/UserLog/Action";
import AppPagination from "../Tools/AppPagination";
import Errors from "../Errors.js";
import AdminSidebar from "../Admin/AdminSideBar";
import { TextField, Button, Box, Grid } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

// Material ui data grid has used for the table
// Initialize the columns for the tables and (field) value is used to show data in a specific column dynamically
export const userLogColumns = [
  {
    field: "username",
    headerName: "Username",
    width: 150,
    editable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>Username</span>,
  },
  {
    field: "httpMethod",
    headerName: "HTTP Method",
    width: 130,
    editable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>HTTP Method</span>,
  },
  {
    field: "endpointMethod",
    headerName: "Endpoint",
    width: 200,
    editable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>Endpoint</span>,
  },
  {
    field: "requestUri",
    headerName: "Request URI",
    width: 250,
    editable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>Request URI</span>,
  },
  {
    field: "ipAddress",
    headerName: "IP Address",
    width: 150,
    editable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>IP Address</span>,
  },
  {
    field: "responseStatus",
    headerName: "Status",
    width: 100,
    editable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>Status</span>,
    renderCell: (params) => {
      const status = params?.row?.responseStatus || 0;
      const statusColor = status >= 200 && status < 300 ? "text-green-600" : status >= 400 ? "text-red-600" : "text-yellow-600";
      return <span className={statusColor}>{status}</span>;
    },
  },
  {
    field: "createdAt",
    headerName: "Timestamp",
    disableColumnMenu: true,
    width: 220,
    editable: false,
    headerAlign: "center",
    align: "center",
    headerClassName: "text-black font-semibold border",
    cellClassName: "text-slate-700 font-normal border",
    renderHeader: (params) => <span>Timestamp</span>,
    renderCell: (params) => {
      return (
        <div className="flex items-center justify-center gap-1">
          <span>
            <MdDateRange className="text-slate-700 text-lg" />
          </span>
          <span>{params?.row?.createdAt}</span>
        </div>
      );
    },
  },
];

const UserLogs = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [filterType, setFilterType] = useState("all"); // "all", "userId", "ipAddress"
  const [userId, setUserId] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  const userLogs = useSelector(
    (store) => store.userLog.userLogs?.data?.activityLogs || []
  );
  const totalPages = useSelector(
    (store) => store.userLog.userLogs?.data?.totalPages || 1
  );
  const loading = useSelector((store) => store.userLog.loadingUserLogs);
  const error = useSelector((store) => store.userLog.errorUserLogs);

  // Handle data fetching based on filter type and page
  useEffect(() => {
    if (filterType === "userId" && userId.trim()) {
      dispatch(getUserLogsByUserId(userId.trim(), page));
    } else if (filterType === "ipAddress" && ipAddress.trim()) {
      dispatch(getUserLogsByIpAddress(ipAddress.trim(), page));
    } else {
      dispatch(getUserLogs(page));
    }
  }, [dispatch, page, filterType]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    if (userId.trim()) {
      setFilterType("userId");
      dispatch(getUserLogsByUserId(userId.trim(), 0));
    } else if (ipAddress.trim()) {
      setFilterType("ipAddress");
      dispatch(getUserLogsByIpAddress(ipAddress.trim(), 0));
    } else {
      setFilterType("all");
      dispatch(getUserLogs(0));
    }
  };

  const handleClear = () => {
    setFilterType("all");
    setUserId("");
    setIpAddress("");
    setPage(0);
    dispatch(getUserLogs(0));
  };

  const rows = userLogs.map((item, index) => {
    // Format the time by using moment npm package
    const formattedDate = moment(item.createdAt).format(
      "MMMM DD, YYYY, hh:mm A"
    );

    // Set the data for each row in the table according to the field name in columns
    return {
      id: item.id || index,
      username: item.username || "-",
      httpMethod: item.httpMethod || "-",
      endpointMethod: item.endpointMethod || "-",
      requestUri: item.requestUri || "-",
      ipAddress: item.ipAddress || "-",
      responseStatus: item.responseStatus || 0,
      createdAt: formattedDate,
    };
  });

  if (error) {
    return (
      <div className="flex min-h-screen bg-slate-100">
        <AdminSidebar />
        <div className="flex-1 flex flex-col">
          <header className="flex items-center justify-between bg-white p-4 shadow">
            <h1 className="text-2xl font-semibold text-indigo-700">User Logs</h1>
            <div className="hidden md:block">
              <span className="text-gray-600 font-medium">Welcome, Admin</span>
            </div>
          </header>
          <main className="p-6 bg-gradient-to-br from-sky-100 to-blue-200 flex-1">
            <Errors message={error?.message || "Error fetching user logs"} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-100">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        <header className="flex items-center justify-between bg-white p-4 shadow">
          <h1 className="text-2xl font-semibold text-indigo-700">User Activity Logs</h1>
          <div className="hidden md:block">
            <span className="text-gray-600 font-medium">Welcome, Admin</span>
          </div>
        </header>
        <main className="p-6 bg-gradient-to-br from-sky-100 to-blue-200 flex-1">
          {/* Filters Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: "white", borderRadius: 2, boxShadow: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="User ID"
                  variant="outlined"
                  size="small"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={ipAddress.trim() !== ""}
                  placeholder="Enter User ID"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="IP Address"
                  variant="outlined"
                  size="small"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  disabled={userId.trim() !== ""}
                  placeholder="Enter IP Address"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                      },
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                    disabled={loading}
                    sx={{
                      borderColor: "#667eea",
                      color: "#667eea",
                      "&:hover": {
                        borderColor: "#5a67d8",
                        backgroundColor: "rgba(102, 126, 234, 0.04)",
                      },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {loading ? (
            <>
              <div className="flex flex-col justify-center items-center h-72">
                <span>
                  <Blocks
                    height="70"
                    width="70"
                    color="#4fa94d"
                    ariaLabel="blocks-loading"
                    wrapperStyle={{}}
                    wrapperClass="blocks-wrapper"
                    visible={true}
                  />
                </span>
                <span>Please wait...</span>
              </div>
            </>
          ) : (
            <>
              <div className="overflow-x-auto w-full mx-auto mb-4">
                <DataGrid
                  className="w-fit mx-auto px-0"
                  rows={rows}
                  columns={userLogColumns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  pageSizeOptions={[10]}
                  disableRowSelectionOnClick
                  disableColumnResize
                  hideFooterPagination
                />
              </div>
              {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                  <AppPagination setPage={setPage} totalPages={totalPages} />
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default UserLogs;

